import { v4 as uuidv4 } from 'uuid';

/**
 * Create a new tab with default empty state.
 */
export function createTab(name = 'New Tab') {
  return {
    id: uuidv4(),
    name,
    widgets: [],
    notes: '',
    timelineMarkers: [],
    rules: [],
    activeRuleIds: [],
  };
}

/**
 * Add a new tab to the workspace layout.
 * Returns a new layoutConfig with the tab appended.
 */
export function addTab(layoutConfig, name = 'New Tab') {
  const tab = createTab(name);
  return {
    ...layoutConfig,
    tabs: [...(layoutConfig.tabs || []), tab],
  };
}

/**
 * Remove a tab by ID. Prevents removing the last tab.
 * If removed tab was defaultTabId or activeTabId, resets to first tab.
 */
export function removeTab(layoutConfig, tabId) {
  const tabs = layoutConfig.tabs || [];
  if (tabs.length <= 1) return layoutConfig;

  const filtered = tabs.filter((t) => t.id !== tabId);
  const result = { ...layoutConfig, tabs: filtered };

  if (result.activeTabId === tabId) {
    result.activeTabId = filtered[0].id;
  }
  if (result.defaultTabId === tabId) {
    result.defaultTabId = filtered[0].id;
  }

  return result;
}

/**
 * Rename a tab.
 */
export function renameTab(layoutConfig, tabId, newName) {
  return {
    ...layoutConfig,
    tabs: (layoutConfig.tabs || []).map((t) =>
      t.id === tabId ? { ...t, name: newName } : t
    ),
  };
}

/**
 * Reorder tabs by moving a tab from one index to another.
 */
export function reorderTabs(layoutConfig, fromIndex, toIndex) {
  const tabs = [...(layoutConfig.tabs || [])];
  if (fromIndex < 0 || fromIndex >= tabs.length || toIndex < 0 || toIndex >= tabs.length) {
    return layoutConfig;
  }
  const [moved] = tabs.splice(fromIndex, 1);
  tabs.splice(toIndex, 0, moved);
  return { ...layoutConfig, tabs };
}

/**
 * Set the default tab (opens on workspace load).
 */
export function setDefaultTab(layoutConfig, tabId) {
  const tab = (layoutConfig.tabs || []).find((t) => t.id === tabId);
  if (!tab) return layoutConfig;
  return { ...layoutConfig, defaultTabId: tabId };
}

/**
 * Set the active tab.
 */
export function setActiveTab(layoutConfig, tabId) {
  const tab = (layoutConfig.tabs || []).find((t) => t.id === tabId);
  if (!tab) return layoutConfig;
  return { ...layoutConfig, activeTabId: tabId };
}

/**
 * Get the active tab object. Falls back to the default tab, then first tab.
 */
export function getActiveTab(layoutConfig) {
  const tabs = layoutConfig.tabs || [];
  if (tabs.length === 0) return null;

  const byActiveId = tabs.find((t) => t.id === layoutConfig.activeTabId);
  if (byActiveId) return byActiveId;

  const byDefaultId = tabs.find((t) => t.id === layoutConfig.defaultTabId);
  if (byDefaultId) return byDefaultId;

  return tabs[0];
}

/**
 * Update a specific tab's data (widgets, notes, markers, rules, etc.).
 */
export function updateTab(layoutConfig, tabId, updates) {
  return {
    ...layoutConfig,
    tabs: (layoutConfig.tabs || []).map((t) =>
      t.id === tabId ? { ...t, ...updates } : t
    ),
  };
}

/** Widget fields that are never a recording reference. */
const NON_RECORDING_WIDGET_KEYS = new Set(['id', 'type']);

/**
 * True when the widget points at this recording.
 *
 * Only the widget types declared `requiresRecording` carry a top-level
 * `recordingId`. The analytics types bind their recording through a settings
 * key instead — `recId`, `recId1`, `recId2` — and leave `recordingId` null, so
 * matching on `recordingId` alone left all of them behind pointing at a
 * recording that no longer exists. Any config value equal to the id counts,
 * which keeps this in step with the API's rule without either side having to
 * know the settings vocabulary.
 */
function widgetReferencesRecording(widget, recordingId) {
  return Object.entries(widget).some(
    ([key, value]) => !NON_RECORDING_WIDGET_KEYS.has(key) && value === recordingId
  );
}

/**
 * Remove widgets for a recording across all tabs.
 */
export function removeRecordingWidgets(layoutConfig, recordingId) {
  return {
    ...layoutConfig,
    tabs: (layoutConfig.tabs || []).map((t) => ({
      ...t,
      widgets: (t.widgets || []).filter((w) => !widgetReferencesRecording(w, recordingId)),
    })),
  };
}
